import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Play } from "lucide-react";
import api from "@/lib/api";

/**
 * Public branded video landing page — /video/:slug
 * Linked from the cold-outreach drip mails. Super clean: logo, the video,
 * one CTA ("Activeer je 10 gratis credits") and the 2 other videos below.
 */
export default function VideoLanding() {
    const { slug } = useParams();
    const [video, setVideo] = useState(null);
    const [others, setOthers] = useState([]);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        setVideo(null);
        setNotFound(false);
        api.get(`/videos/${slug}`)
            .then((r) => {
                setVideo(r.data.video);
                setOthers(r.data.others || []);
                window.scrollTo(0, 0);
            })
            .catch(() => setNotFound(true));
    }, [slug]);

    return (
        <div className="min-h-screen bg-[#FAFAFA] text-zinc-950 font-body" data-testid="video-landing-page">
            {/* Subtle gold glow, same brand language as the main site */}
            <div
                className="fixed -top-40 -right-40 h-[500px] w-[500px] rounded-full blur-3xl opacity-[0.14] pointer-events-none"
                style={{ background: "#E5A93D" }}
            />
            <div className="relative max-w-4xl mx-auto px-5 md:px-8 py-10 md:py-16">
                {/* Brand header */}
                <Link to="/" className="inline-block mb-10 md:mb-14" data-testid="video-brand-link">
                    <div className="text-[11px] uppercase tracking-[0.35em] text-[#E5A93D] font-bold">Social Factory</div>
                    <div className="text-sm text-zinc-500 mt-1">Vastgoed Studio Pro</div>
                </Link>

                {notFound ? (
                    <div className="text-center py-24 text-zinc-500" data-testid="video-not-found">
                        Video niet gevonden.
                        <div className="mt-4">
                            <Link to="/" className="text-[#E5A93D] hover:underline text-sm">Naar de homepage</Link>
                        </div>
                    </div>
                ) : !video ? (
                    <div className="text-center py-24 text-zinc-500 text-sm">Laden…</div>
                ) : (
                    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                        <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight mb-8 text-balance" data-testid="video-title">
                            {video.title}
                        </h1>
                        <div className="rounded-md overflow-hidden border border-zinc-200 bg-zinc-50 shadow-2xl mb-10">
                            <video
                                key={video.url}
                                src={video.url}
                                controls
                                playsInline
                                preload="metadata"
                                className="w-full aspect-video bg-zinc-50"
                                data-testid="video-player"
                            />
                        </div>

                        {/* CTA */}
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-16">
                            <Link
                                to="/register"
                                className="inline-flex items-center gap-2 h-12 px-7 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm transition-colors active:scale-95"
                                data-testid="video-cta-register"
                            >
                                Activeer je 10 gratis credits <ArrowRight className="h-4 w-4" />
                            </Link>
                            <p className="text-sm text-zinc-500">
                                Vastgoedvideo's én AI-fototools — alles in 1 platform.
                            </p>
                        </div>

                        {/* Other videos */}
                        {others.length > 0 && (
                            <div data-testid="video-others">
                                <div className="text-xs uppercase tracking-[0.25em] text-zinc-500 font-semibold mb-5">Bekijk ook</div>
                                <div className="grid sm:grid-cols-2 gap-4">
                                    {others.map((v) => (
                                        <Link
                                            key={v.slug}
                                            to={`/video/${v.slug}`}
                                            className="group border border-zinc-200 rounded-md p-5 bg-white hover:border-[#E5A93D]/50 transition-colors"
                                            data-testid={`video-other-${v.slug}`}
                                        >
                                            <div className="h-10 w-10 rounded-full bg-[#E5A93D]/10 border border-[#E5A93D]/30 flex items-center justify-center mb-4 group-hover:bg-[#E5A93D]/20 transition-colors">
                                                <Play className="h-4 w-4 text-[#E5A93D]" fill="currentColor" />
                                            </div>
                                            <div className="font-semibold text-sm leading-snug">{v.title}</div>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}
                    </motion.div>
                )}

                <div className="mt-20 pt-6 border-t border-zinc-100 text-xs text-zinc-400">
                    <a href="https://vastgoed.social-factory.be" className="hover:text-zinc-500 transition-colors">vastgoed.social-factory.be</a>
                </div>
            </div>
        </div>
    );
}
